import { MaybePromise } from "@/types/maybe-promise";
import { WEEKDAYS } from "@/utils/dates";
import { StepperSubmitHandler } from "@/utils/stepper";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import z from "zod";
import {
  CampaignPeriodField,
  campaignPeriodFieldValueSchema,
} from "./CampaignPeriodField";
import {
  CampaignSchedulesField,
  createCampaignSchedulesFieldsSchema,
} from "./CampaignSchedulesFields";

export const createSchedulesAndPeriodFormSchema = (defaultValues?: {
  schedules: Partial<Record<(typeof WEEKDAYS)[number]["id"], number | null>>;
}) =>
  z.object({
    schedules: createCampaignSchedulesFieldsSchema(defaultValues?.schedules),
    period: campaignPeriodFieldValueSchema,
  });

export type SchedulesAndPeriodFormValues = z.infer<
  ReturnType<typeof createSchedulesAndPeriodFormSchema>
>;

export function SchedulesAndPeriodForm({
  defaultValues,
  values,
  onSubmit,
  setSubmitHandler,
  autoSubmit,
}: {
  defaultValues: SchedulesAndPeriodFormValues;
  values?: SchedulesAndPeriodFormValues;
  onSubmit: (values: SchedulesAndPeriodFormValues) => MaybePromise<void>;
  setSubmitHandler?: (submitHandler: StepperSubmitHandler) => void;
  autoSubmit?: boolean;
}) {
  const {
    control,
    handleSubmit,
    formState: {
      isDirty,
      isValid,
      isSubmitted,
      isSubmitting,
      defaultValues: formCurrentDefaultValues,
    },
    setValue,
    reset,
    trigger,
  } = useForm({
    resolver: zodResolver(createSchedulesAndPeriodFormSchema(defaultValues)),
    defaultValues,
  });

  useEffect(() => {
    if (defaultValues === formCurrentDefaultValues) return;
    reset(defaultValues, { keepValues: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues]);

  useEffect(() => {
    if (!values) return;
    setValue("schedules", values.schedules);
    setValue("period", values.period);
  }, [values, setValue]);

  const submit = useMemo(
    () => handleSubmit(onSubmit),
    [handleSubmit, onSubmit],
  );

  useEffect(() => {
    setSubmitHandler?.({
      submit,
      isDisabled: (!isValid && isSubmitted) || isSubmitting,
      isDirty,
      isPending: isSubmitting,
    });
  }, [setSubmitHandler, submit, isValid, isSubmitted, isSubmitting, isDirty]);

  const autoSubmittedRef = useRef(false);

  useEffect(() => {
    if (autoSubmittedRef.current) return;

    if (autoSubmit) {
      autoSubmittedRef.current = true;
      submit();
    }
  }, [autoSubmit, submit]);

  return (
    <form className="flex flex-col items-center gap-y-10" onSubmit={submit}>
      <CampaignSchedulesField
        control={control}
        name="schedules"
        defaultValues={defaultValues.schedules}
      />

      <CampaignPeriodField control={control} name="period" trigger={trigger} />
    </form>
  );
}
